import * as React from "react"
import { CommandPalette } from "./CommandPalette"
import type { PaletteCommand } from "./types"

interface CommandPaletteContextValue {
	open: boolean
	setOpen: (open: boolean) => void
	register: (scopeId: string, commands: PaletteCommand[]) => void
	unregister: (scopeId: string) => void
	contextualCommands: PaletteCommand[]
}

const CommandPaletteContext =
	React.createContext<CommandPaletteContextValue | null>(null)

export function useCommandPalette() {
	const context = React.useContext(CommandPaletteContext)
	if (!context) {
		throw new Error(
			"useCommandPalette must be used within CommandPaletteProvider",
		)
	}
	return context
}

export function CommandPaletteProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const [open, setOpen] = React.useState(false)
	const [scopes, setScopes] = React.useState<Record<string, PaletteCommand[]>>(
		{},
	)

	const register = React.useCallback(
		(scopeId: string, commands: PaletteCommand[]) => {
			setScopes((current) => ({ ...current, [scopeId]: commands }))
		},
		[],
	)

	const unregister = React.useCallback((scopeId: string) => {
		setScopes((current) => {
			const { [scopeId]: _removed, ...rest } = current
			return rest
		})
	}, [])

	React.useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
				event.preventDefault()
				setOpen((current) => !current)
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [])

	const contextualCommands = React.useMemo(
		() => Object.values(scopes).flat(),
		[scopes],
	)

	const value = React.useMemo(
		() => ({ open, setOpen, register, unregister, contextualCommands }),
		[open, register, unregister, contextualCommands],
	)

	return (
		<CommandPaletteContext.Provider value={value}>
			{children}
			<CommandPalette
				open={open}
				onOpenChange={setOpen}
				contextualCommands={contextualCommands}
			/>
		</CommandPaletteContext.Provider>
	)
}
